import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Edit2, Trash2, Plus, X, Download, Upload, RefreshCw, CheckCircle2, Send, FileText } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";

/* ============= USERS ============= */
export function B2BUsersPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/b2b/users").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const setStatus = async (u, status) => {
    try { await api.put(`/admin/b2b/users/${u.id}`, { status }); toast.success("Status aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Eliminare ${u.email}? L'azione è irreversibile.`)) return;
    try { await api.delete(`/admin/b2b/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resend = async (u) => {
    try { await api.post(`/admin/b2b/users/${u.id}/resend-invite`); toast.success("Nuovo link di accesso inviato"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) return <B2BUserForm initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-ink">{items.length} utenti B2B totali</div>
        <button onClick={() => setEditing("new")} className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-xs uppercase tracking-editorial">
          <Plus className="h-3.5 w-3.5" /> Nuovo utente
        </button>
      </div>
      <div className="bg-white border border-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream-warm text-[10px] uppercase tracking-editorial text-muted-ink">
            <tr>
              <th className="text-left px-4 py-2">Email</th>
              <th className="text-left px-4 py-2">Nome</th>
              <th className="text-left px-4 py-2">Azienda</th>
              <th className="text-left px-4 py-2">Tipo</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Ultimo accesso</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-soft">
                <td className="px-4 py-3 text-brand">{u.email}</td>
                <td className="px-4 py-3">{u.first_name} {u.last_name}</td>
                <td className="px-4 py-3">{u.company}</td>
                <td className="px-4 py-3 text-xs">{u.account_type}</td>
                <td className="px-4 py-3">
                  <select value={u.status} onChange={(e) => setStatus(u, e.target.value)} className="text-xs border border-soft bg-white px-2 py-1 rounded-none">
                    <option value="pending">In attesa</option>
                    <option value="active">Attivo</option>
                    <option value="suspended">Sospeso</option>
                    <option value="disabled">Disattivato</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-muted-ink">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("it-IT") : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(u)} className="p-1.5 hover:bg-cream-warm" title="Modifica"><Edit2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => resend(u)} className="p-1.5 hover:bg-cream-warm" title="Reinvia link"><RefreshCw className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(u)} className="p-1.5 hover:bg-cream-warm text-destructive" title="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function B2BUserForm({ initial, onClose, onSaved }) {
  const empty = { email: "", first_name: "", last_name: "", company: "", phone: "", vat_number: "", account_type: "reseller", admin_notes: "", group_ids: [], allowed_products: [] };
  const [form, setForm] = useState(initial || empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/admin/b2b/groups").then((r) => setGroups(r.data || []));
    api.get("/products").then((r) => setProducts(r.data || []));
  }, []);
  const toggleProd = (slug) => set("allowed_products", (form.allowed_products || []).includes(slug)
    ? form.allowed_products.filter((x) => x !== slug)
    : [...(form.allowed_products || []), slug]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (initial?.id) await api.put(`/admin/b2b/users/${initial.id}`, form);
      else await api.post("/admin/b2b/users", form);
      toast.success(initial?.id ? "Utente aggiornato" : "Utente creato — email di accesso inviata");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-soft p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-2xl text-brand">{initial?.id ? `Modifica: ${initial.email}` : "Nuovo utente B2B"}</h3>
        <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F l="Email"><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></F>
        <F l="Tipologia account">
          <select value={form.account_type} onChange={(e) => set("account_type", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="reseller">Rivenditore</option><option value="distributor">Distributore</option>
            <option value="vet">Veterinario</option><option value="pharmacy">Farmacia</option>
            <option value="parapharmacy">Parafarmacia</option><option value="pet_shop">Pet shop</option>
            <option value="agent">Agente commerciale</option><option value="collaborator">Collaboratore</option>
            <option value="other">Altro</option>
          </select>
        </F>
        <F l="Nome"><Input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></F>
        <F l="Cognome"><Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></F>
        <F l="Azienda" c="md:col-span-2"><Input required value={form.company} onChange={(e) => set("company", e.target.value)} /></F>
        <F l="Telefono"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></F>
        <F l="P.IVA"><Input value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} /></F>
        <F l="Gruppi" c="md:col-span-2">
          <div className="flex flex-wrap gap-2">
            {groups.map((g) => (
              <label key={g.id} className={`px-3 py-1.5 border text-xs cursor-pointer ${form.group_ids?.includes(g.id) ? "bg-brand text-white border-brand" : "border-soft text-brand"}`}>
                <input type="checkbox" className="sr-only" checked={form.group_ids?.includes(g.id)}
                       onChange={() => set("group_ids", form.group_ids?.includes(g.id) ? form.group_ids.filter((x) => x !== g.id) : [...(form.group_ids || []), g.id])} />
                {g.name}
              </label>
            ))}
          </div>
        </F>
        <F l="Prodotti autorizzati (documentazione B2B)" c="md:col-span-2">
          <p className="text-xs text-muted-ink mb-2 italic">La documentazione "BioNutrix (azienda)" è sempre visibile a tutti gli utenti B2B attivi.</p>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1.5 border border-human bg-human-soft text-human text-xs">BioNutrix (azienda) — sempre attivo</span>
            {products.map((p) => (
              <label key={p.slug} className={`px-3 py-1.5 border text-xs cursor-pointer ${(form.allowed_products || []).includes(p.slug) ? "bg-brand text-white border-brand" : "border-soft text-brand"}`}>
                <input type="checkbox" className="sr-only" checked={(form.allowed_products || []).includes(p.slug)}
                       onChange={() => toggleProd(p.slug)} />
                {p.name}
              </label>
            ))}
          </div>
        </F>
        <F l="Note interne (non visibili all'utente)" c="md:col-span-2">
          <textarea rows={2} className="w-full border border-soft bg-white p-2 text-sm" value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} />
        </F>
      </div>
      {!initial?.id && (
        <p className="text-xs text-muted-ink italic">Alla creazione verrà inviata una email con un link temporaneo (48h) per impostare la password.</p>
      )}
      <div className="flex justify-end gap-2 pt-3 border-t border-soft">
        <button type="button" onClick={onClose} className="px-5 py-2 border border-soft text-xs uppercase tracking-editorial">Annulla</button>
        <button type="submit" disabled={loading} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial disabled:opacity-50">{loading ? "…" : "Salva"}</button>
      </div>
    </form>
  );
}

/* ============= ACCESS REQUESTS ============= */
export function B2BRequestsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/b2b/access-requests").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    if (!window.confirm(`Approvare ${r.email}? Verrà creato un utente e inviata l'email di accesso.`)) return;
    try {
      await api.post(`/admin/b2b/access-requests/${r.id}/approve`, { account_type: r.account_type || "reseller", group_ids: [] });
      toast.success("Richiesta approvata — utente creato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (r, status) => {
    try { await api.put(`/admin/b2b/access-requests/${r.id}/status?status=${status}`); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-soft">
      {items.length === 0 ? <div className="p-6 text-muted-ink text-sm">Nessuna richiesta.</div> : items.map((r) => (
        <div key={r.id} className="p-4 border-b border-soft last:border-b-0 grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
          <div className="md:col-span-2">
            <div className="text-brand font-medium">{r.first_name} {r.last_name} · {r.company}</div>
            <div className="text-xs text-muted-ink">{r.email} · {r.phone}</div>
            <div className="text-xs text-muted-ink">{r.account_type} · {r.city} ({r.province}) · P.IVA {r.vat_number}</div>
            {r.motivation && <p className="text-sm text-muted-ink mt-2">"{r.motivation}"</p>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-editorial text-muted-ink mb-1">Status</div>
            <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} className="text-xs border border-soft bg-white px-2 py-1">
              <option value="new">Nuova</option>
              <option value="reviewing">In valutazione</option>
              <option value="approved">Approvata</option>
              <option value="rejected">Rifiutata</option>
            </select>
            <div className="text-[10px] text-muted-ink mt-2">Ricevuta: {new Date(r.created_at).toLocaleString("it-IT")}</div>
          </div>
          <div className="text-right">
            {r.status !== "approved" && (
              <button onClick={() => approve(r)} className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-xs uppercase tracking-editorial">
                <CheckCircle2 className="h-3.5 w-3.5" /> Approva
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============= DOCUMENTS ============= */
export function B2BDocsPanel() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/b2b/documents").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/admin/b2b/categories").then((r) => setCats(r.data || []));
    api.get("/admin/b2b/users").then((r) => setUsers(r.data || []));
    api.get("/admin/b2b/groups").then((r) => setGroups(r.data || []));
    api.get("/products").then((r) => setProducts(r.data || []));
  }, []);

  const del = async (d) => {
    if (!window.confirm(`Eliminare "${d.title}"?`)) return;
    await api.delete(`/admin/b2b/documents/${d.id}`);
    toast.success("Documento eliminato");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-ink">{items.length} documenti totali</div>
        <button onClick={() => setShowUpload(true)} className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-2 text-xs uppercase tracking-editorial">
          <Upload className="h-3.5 w-3.5" /> Carica PDF
        </button>
      </div>
      {showUpload && <UploadForm cats={cats} users={users} groups={groups} products={products} onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />}
      {editing && <EditDocForm doc={editing} cats={cats} users={users} groups={groups} products={products} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <div className="bg-white border border-soft overflow-x-auto mt-4">
        <table className="w-full text-sm">
          <thead className="bg-cream-warm text-[10px] uppercase tracking-editorial text-muted-ink">
            <tr><th className="text-left px-4 py-2">Titolo</th><th className="text-left px-4 py-2">Prodotto</th><th className="text-left px-4 py-2">Categoria</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Download</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((d) => {
              const prodLabel = d.product_slug === "bionutrix-company" ? "BioNutrix (azienda)" : (products.find((p) => p.slug === d.product_slug)?.name || "—");
              return (
              <tr key={d.id} className="border-t border-soft">
                <td className="px-4 py-3 max-w-xs">
                  <div className="text-brand">{d.title}</div>
                  <div className="text-xs text-muted-ink">{d.filename} · {(d.size/1024/1024).toFixed(2)}MB</div>
                </td>
                <td className="px-4 py-3 text-xs">{prodLabel}</td>
                <td className="px-4 py-3 text-xs">{cats.find((c) => c.id === d.category_id)?.name || "—"}</td>
                <td className="px-4 py-3 text-xs">{d.status}</td>
                <td className="px-4 py-3 text-xs">{d.download_count || 0}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(d)} className="p-1.5 hover:bg-cream-warm"><Edit2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(d)} className="p-1.5 hover:bg-cream-warm text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>);
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UploadForm({ cats, users, groups, products, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", product_slug: "bionutrix-company", category_id: "", language: "it", version: "", document_date: "", keywords: "", status: "published" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Seleziona un PDF"); return; }
    if (!form.product_slug) { toast.error("Seleziona un prodotto"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title || file.name);
      fd.append("description", form.description);
      fd.append("product_slug", form.product_slug);
      fd.append("category_id", form.category_id);
      fd.append("language", form.language);
      fd.append("version", form.version);
      fd.append("document_date", form.document_date);
      fd.append("keywords", form.keywords);
      fd.append("permission_type", "all");
      fd.append("status", form.status);
      const token = localStorage.getItem("bnx_token");
      await fetch(`${API}/admin/b2b/documents`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => { if (!r.ok) throw new Error(); });
      toast.success("Documento caricato");
      onSaved();
    } catch (err) { toast.error("Errore caricamento"); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={save} className="bg-white border border-soft p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-2xl text-brand">Carica nuovo PDF</h3>
        <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F l="File PDF (max 25MB)" c="md:col-span-2">
          <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" required />
        </F>
        <F l="Titolo" c="md:col-span-2"><Input required value={form.title} onChange={(e) => set("title", e.target.value)} /></F>
        <F l="Prodotto (a chi si riferisce il documento)" c="md:col-span-2">
          <select required value={form.product_slug} onChange={(e) => set("product_slug", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="bionutrix-company">BioNutrix (azienda) — visibile a tutti</option>
            {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        </F>
        <F l="Categoria (opzionale)">
          <select value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="">— Nessuna —</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </F>
        <F l="Lingua">
          <select value={form.language} onChange={(e) => set("language", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="it">Italiano</option><option value="en">English</option>
          </select>
        </F>
        <F l="Versione"><Input value={form.version} onChange={(e) => set("version", e.target.value)} placeholder="1.0" /></F>
        <F l="Data documento"><Input type="date" value={form.document_date} onChange={(e) => set("document_date", e.target.value)} /></F>
        <F l="Parole chiave (separate da virgola)" c="md:col-span-2"><Input value={form.keywords} onChange={(e) => set("keywords", e.target.value)} /></F>
        <F l="Descrizione" c="md:col-span-2"><textarea rows={3} className="w-full border border-soft bg-white p-2 text-sm" value={form.description} onChange={(e) => set("description", e.target.value)} /></F>
        <F l="Status">
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="published">Pubblicato</option><option value="draft">Bozza</option><option value="hidden">Nascosto</option>
          </select>
        </F>
      </div>
      <p className="text-xs text-muted-ink italic">La visibilità del documento è determinata dal prodotto scelto: l'utente vedrà il documento solo se ha quel prodotto tra quelli autorizzati (o se è nella categoria "BioNutrix azienda").</p>
      <div className="flex justify-end gap-2 pt-3 border-t border-soft">
        <button type="button" onClick={onClose} className="px-5 py-2 border border-soft text-xs uppercase tracking-editorial">Annulla</button>
        <button type="submit" disabled={loading} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial disabled:opacity-50">{loading ? "Caricamento…" : "Carica PDF"}</button>
      </div>
    </form>
  );
}

function EditDocForm({ doc, cats, users, groups, products, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: doc.title, description: doc.description || "", product_slug: doc.product_slug || "bionutrix-company",
    category_id: doc.category_id || "", language: doc.language || "it", version: doc.version || "",
    document_date: doc.document_date || "", keywords: doc.keywords || [], status: doc.status || "published",
    permission_type: doc.permission_type || "all", order: doc.order || 0, filename: doc.filename,
  });
  const [loading, setLoading] = useState(false);
  const replaceInput = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/admin/b2b/documents/${doc.id}`, form);
      toast.success("Salvato");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };
  const replace = async () => {
    const file = replaceInput.current?.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("bnx_token");
    const resp = await fetch(`${API}/admin/b2b/documents/${doc.id}/replace`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
    if (resp.ok) { toast.success("PDF sostituito"); onSaved(); } else toast.error("Errore sostituzione");
  };

  return (
    <form onSubmit={save} className="bg-white border border-soft p-6 space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-xl text-brand">Modifica: {doc.title}</h3>
        <button type="button" onClick={onClose}><X className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <F l="Titolo" c="md:col-span-2"><Input value={form.title} onChange={(e) => set("title", e.target.value)} /></F>
        <F l="Prodotto" c="md:col-span-2">
          <select value={form.product_slug} onChange={(e) => set("product_slug", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="bionutrix-company">BioNutrix (azienda)</option>
            {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        </F>
        <F l="Categoria">
          <select value={form.category_id || ""} onChange={(e) => set("category_id", e.target.value || null)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="">—</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </F>
        <F l="Status">
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="published">Pubblicato</option><option value="draft">Bozza</option><option value="hidden">Nascosto</option>
          </select>
        </F>
        <F l="Descrizione" c="md:col-span-2"><textarea rows={2} className="w-full border border-soft bg-white p-2 text-sm" value={form.description} onChange={(e) => set("description", e.target.value)} /></F>
        <F l="Sostituisci PDF" c="md:col-span-2">
          <div className="flex gap-2">
            <input ref={replaceInput} type="file" accept="application/pdf" className="text-sm" />
            <button type="button" onClick={replace} className="rounded-full bg-brand text-white px-4 py-1.5 text-xs uppercase">Sostituisci</button>
          </div>
        </F>
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-soft">
        <button type="button" onClick={onClose} className="px-5 py-2 border border-soft text-xs uppercase tracking-editorial">Annulla</button>
        <button type="submit" disabled={loading} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial disabled:opacity-50">Salva</button>
      </div>
    </form>
  );
}
/* ============= CATEGORIES / GROUPS ============= */
export function B2BCatsGroupsPanel() {
  const [cats, setCats] = useState([]);
  const [groups, setGroups] = useState([]);
  const load = () => {
    api.get("/admin/b2b/categories").then((r) => setCats(r.data || []));
    api.get("/admin/b2b/groups").then((r) => setGroups(r.data || []));
  };
  useEffect(() => { load(); }, []);
  const [newCat, setNewCat] = useState("");
  const [newGroup, setNewGroup] = useState("");

  const addCat = async () => {
    if (!newCat.trim()) return;
    await api.post("/admin/b2b/categories", { name: newCat, order: cats.length });
    setNewCat(""); load();
  };
  const addGroup = async () => {
    if (!newGroup.trim()) return;
    await api.post("/admin/b2b/groups", { name: newGroup });
    setNewGroup(""); load();
  };
  const delCat = async (c) => {
    const move = prompt("La categoria potrebbe contenere documenti. Inserisci l'ID categoria di destinazione (lascia vuoto se vuota):");
    try {
      await api.delete(`/admin/b2b/categories/${c.id}${move ? `?move_to=${move}` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delGroup = async (g) => {
    if (!window.confirm(`Eliminare gruppo ${g.name}?`)) return;
    await api.delete(`/admin/b2b/groups/${g.id}`); load();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-display text-xl text-brand mb-3">Categorie</h3>
        <div className="bg-white border border-soft">
          {cats.map((c) => (
            <div key={c.id} className="p-3 border-b border-soft last:border-b-0 flex justify-between items-center">
              <div><span className="text-brand text-sm">{c.name}</span> <span className="text-xs text-muted-ink font-mono ml-2">{c.id.slice(0,8)}</span></div>
              <button onClick={() => delCat(c)} className="text-destructive text-xs hover:bg-cream-warm p-1"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nuova categoria" className="rounded-none border-soft" />
          <button onClick={addCat} className="rounded-full bg-brand text-white px-4 text-xs uppercase">Aggiungi</button>
        </div>
      </div>
      <div>
        <h3 className="font-display text-xl text-brand mb-3">Gruppi</h3>
        <div className="bg-white border border-soft">
          {groups.map((g) => (
            <div key={g.id} className="p-3 border-b border-soft last:border-b-0 flex justify-between items-center">
              <div><span className="text-brand text-sm">{g.name}</span> <span className="text-xs text-muted-ink font-mono ml-2">{g.id.slice(0,8)}</span></div>
              <button onClick={() => delGroup(g)} className="text-destructive text-xs hover:bg-cream-warm p-1"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input value={newGroup} onChange={(e) => setNewGroup(e.target.value)} placeholder="Nuovo gruppo" className="rounded-none border-soft" />
          <button onClick={addGroup} className="rounded-full bg-brand text-white px-4 text-xs uppercase">Aggiungi</button>
        </div>
      </div>
    </div>
  );
}

/* ============= COMMUNICATIONS ============= */
export function B2BCommsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", link: "", audience_type: "all", status: "draft" });
  const load = () => api.get("/admin/b2b/communications").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    await api.post("/admin/b2b/communications", form);
    toast.success("Comunicazione creata");
    setForm({ title: "", body: "", link: "", audience_type: "all", status: "draft" });
    load();
  };
  const del = async (c) => { if (window.confirm("Eliminare?")) { await api.delete(`/admin/b2b/communications/${c.id}`); load(); } };
  const togglePublish = async (c) => {
    await api.put(`/admin/b2b/communications/${c.id}`, { ...c, status: c.status === "published" ? "draft" : "published" });
    load();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-display text-xl text-brand mb-3">Comunicazioni pubblicate</h3>
        <div className="bg-white border border-soft">
          {items.length === 0 && <div className="p-6 text-muted-ink text-sm">Nessuna comunicazione.</div>}
          {items.map((c) => (
            <div key={c.id} className="p-4 border-b border-soft last:border-b-0">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="text-brand font-medium">{c.title}</div>
                  <div className="text-xs text-muted-ink">{c.audience_type} · {c.status}</div>
                  <p className="text-sm text-muted-ink mt-1">{c.body}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => togglePublish(c)} className="p-1.5 hover:bg-cream-warm text-xs">
                    {c.status === "published" ? "Bozza" : "Pubblica"}
                  </button>
                  <button onClick={() => del(c)} className="p-1.5 hover:bg-cream-warm text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <form onSubmit={save} className="bg-white border border-soft p-5 space-y-3">
        <h3 className="font-display text-xl text-brand">Nuova comunicazione</h3>
        <F l="Titolo"><Input required value={form.title} onChange={(e) => set("title", e.target.value)} /></F>
        <F l="Testo"><textarea rows={4} required className="w-full border border-soft bg-white p-2 text-sm" value={form.body} onChange={(e) => set("body", e.target.value)} /></F>
        <F l="Link (opzionale)"><Input value={form.link} onChange={(e) => set("link", e.target.value)} /></F>
        <F l="Destinatari">
          <select value={form.audience_type} onChange={(e) => set("audience_type", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="all">Tutti gli utenti B2B</option>
            <option value="group">Un gruppo</option>
            <option value="user">Un singolo utente</option>
            <option value="company">Una specifica azienda</option>
          </select>
        </F>
        <F l="Status">
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full h-9 border border-soft bg-white px-3 text-sm">
            <option value="draft">Bozza</option><option value="published">Pubblicata</option>
          </select>
        </F>
        <button type="submit" className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial">Crea</button>
      </form>
    </div>
  );
}

/* ============= ACTIVITY LOG ============= */
export function B2BActivityPanel() {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState("");
  const load = () => api.get("/admin/b2b/activity-log", { params: action ? { action } : {} }).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, [action]);
  const download = () => {
    const token = localStorage.getItem("bnx_token");
    fetch(`${API}/admin/b2b/activity-log/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((csv) => {
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `bionutrix-b2b-activity-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
      });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <select value={action} onChange={(e) => setAction(e.target.value)} className="h-9 border border-soft bg-white px-3 text-sm">
          <option value="">Tutte le azioni</option>
          <option value="login_success">Login riusciti</option>
          <option value="login_failed">Login falliti</option>
          <option value="document_downloaded">Download documenti</option>
          <option value="document_uploaded">Caricamenti</option>
          <option value="user_created">Utenti creati</option>
          <option value="password_changed">Password cambiate</option>
        </select>
        <button onClick={download} className="inline-flex items-center gap-1.5 rounded-full border border-soft px-4 py-2 text-xs uppercase tracking-editorial hover:bg-cream-warm">
          <Download className="h-3.5 w-3.5" /> Esporta CSV
        </button>
      </div>
      <div className="bg-white border border-soft overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream-warm text-[10px] uppercase tracking-editorial text-muted-ink">
            <tr><th className="text-left px-4 py-2">Data</th><th className="text-left px-4 py-2">Azione</th><th className="text-left px-4 py-2">Utente</th><th className="text-left px-4 py-2">Documento</th><th className="text-left px-4 py-2">Dettagli</th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-soft">
                <td className="px-4 py-2 text-xs">{new Date(r.at).toLocaleString("it-IT")}</td>
                <td className="px-4 py-2 text-xs font-mono">{r.action}</td>
                <td className="px-4 py-2 text-xs">{r.user_id ? r.user_id.slice(0,8) : (r.admin_id ? `admin:${r.admin_id.slice(0,8)}` : "—")}</td>
                <td className="px-4 py-2 text-xs">{r.document_id ? r.document_id.slice(0,8) : "—"}</td>
                <td className="px-4 py-2 text-xs">{r.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============= UTILS ============= */
function F({ l, c = "", children }) {
  return (
    <label className={`block ${c}`}>
      <span className="block text-xs uppercase tracking-editorial text-muted-ink mb-1.5">{l}</span>
      {children}
    </label>
  );
}
